import React, { useContext, useRef, useState } from "react"
import tw, { styled, css, theme } from "twin.macro"
import { ReactComponent as ArrowDownCircleSVG } from "../../assets/nav/arrow-down-circle.svg"
import { useTranslation } from "next-i18next"
import { AuthContext, RedirectContext } from "../../utils/context"
import { useClickOutside } from "../../utils/hooks/useClickOutside"
import Link from "next/link"
import { Routes } from "../../routes"
import { ProfileIcon } from "./profileIcon"
import { NavBarProp } from "."
import { useGetManagerLazyQuery } from "../../graphql/generated/graphql"
import { useEffectAsync } from "../../utils/hooks/useEffectAsync"

interface StyledNavItemsProps {
  showMenu?: boolean
  isblack?: boolean
}
const Nav = styled("ul")(({ showMenu, isblack }: StyledNavItemsProps) => [
  showMenu
    ? tw`absolute top-0 left-0 w-full bg-background tablet-lg:(relative flex bg-base-6 items-center justify-end w-auto)`
    : tw`flex flex-row items-center`,
  isblack ? tw`tablet-lg:(bg-base-6)` : tw`tablet-lg:(bg-background)`,
])
const Menu = styled("ul")(({ showMenu }: StyledNavItemsProps) => [
  tw`bg-background py-0 border-t border-base-2 relative w-full divide-y divide-base-2 tablet-lg:(py-2 border divide-none absolute right-0 top-14 w-32 space-y-2 rounded-md min-w-card)`,
  css`
    li:last-child a {
      color: red;
    }
  `,
  showMenu ? tw`tablet-lg:(flex flex-col items-center)` : tw`hidden`,
])
const Item = styled("li")(() => [
  tw`w-full flex flex-col relative text-md hover:(text-base-6 cursor-pointer font-bold)`,
])
const ArrowDownCircle = styled.li(({ showMenu: showmenu, isblack }: StyledNavItemsProps) => [
  tw`hidden absolute w-screen left-0 right-0 top-0 cursor-pointer`,
  tw`tablet-lg:(flex relative w-auto space-x-4) desktop-lg:(space-x-12)`,
  css`
    rect {
      stroke: ${isblack ? "#ffffff" : showmenu ? "#BC3EF7" : "#1C1C1C"};
    }
    path {
      fill: ${isblack ? "#ffffff" : showmenu ? "#BC3EF7" : "#1C1C1C"};
    }
    &:hover {
      rect {
        stroke: ${theme(`colors.purple`)};
      }
      path {
        fill: ${theme(`colors.purple`)};
      }
    }
  `,
])
const StyledMask = styled("div")(({ showMenu: showmenu }: StyledNavItemsProps) => [
  tw`absolute bg-base-6 opacity-80 w-screen h-screen left-0 right-0 top-0 bottom-0 bg-opacity-80 cursor-pointer block`,
  tw`tablet-lg:hidden`,
  showmenu ? tw`block` : tw`hidden`,
])

export enum EditProfilePage {
  Edit = "edit",
  Settings = "settings",
  Payout = "payout",
  Admin = "admin",
}
export const ProfileNav = ({ isblack }: NavBarProp) => {
  const ref = useRef(null)
  const { preflightData } = useContext(AuthContext)
  const { getData } = useContext(RedirectContext)
  const [showMenu, setShowMenu] = useState<boolean>(false)
  const { t } = useTranslation("nav")
  const [getManager, { data }] = useGetManagerLazyQuery()
  const manager = data?.Manager.manager
  const redirectManagerId = getData().data.managerId
  useClickOutside(ref, () => setShowMenu(false))

  const menuItems = [
    {
      text: t("profile_menu.edit_profile"),
      href: { pathname: Routes.EditProfile, query: { page: EditProfilePage.Edit } },
    },
    ...(manager
      ? [
          {
            text: t("profile_menu.manage_artists"),
            href: { pathname: Routes.ManagerDashboard },
          },
        ]
      : []),
    {
      text: t("profile_menu.settings"),
      href: { pathname: Routes.EditProfile, query: { page: EditProfilePage.Settings } },
    },
    // {
    //   text: t("profile_menu.payout"),
    //   href: { pathname: Routes.EditProfile, query: { page: EditProfilePage.Payout } },
    // },
    {
      text: t("profile_menu.log_out"),
      href: { pathname: Routes.Logout },
    },
  ]

  useEffectAsync(async () => {
    if (redirectManagerId) {
      await getManager({ variables: { managerId: redirectManagerId } })
    }
  }, [redirectManagerId])

  return (
    <>
      <StyledMask showMenu={showMenu} ref={ref} />
      <Nav data-testid="navigation" showMenu={showMenu} isblack={isblack} ref={ref}>
        <li title={t("nav_2_title")} tw="w-full flex flex-col items-center px-2">
          <ProfileIcon {...{ preflightData, isblack, showMenu, setShowMenu }} />
          <Menu data-testid="sub-navigation" showMenu={showMenu}>
            {menuItems.map((item, index) => (
              <Item key={index}>
                <Link href={item.href} passHref>
                  <a tw="py-5 text-base-5 block text-center whitespace-nowrap tablet:(py-3) hover:(font-bold !no-underline)">
                    {item.text}
                  </a>
                </Link>
              </Item>
            ))}
          </Menu>
        </li>
        <ArrowDownCircle isblack={isblack} showMenu={showMenu} onClick={() => setShowMenu(!showMenu)}>
          <ArrowDownCircleSVG />
        </ArrowDownCircle>
      </Nav>
    </>
  )
}
